import React, { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { useWeather } from '../../context';
import { StormReportsLayer } from './StormReportsLayer';
import { OpenMeteoLayer } from './OpenMeteoLayer';
import { TropicalCycloneLayer } from './TropicalCycloneLayer';
import { mapConfig } from '../../config/mapbox';
import { initializeMapBounds } from '../../utils/mapHelpers';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';

export function MapView() {
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<mapboxgl.Map | null>(null);
  const drawInstance = useRef<MapboxDraw | null>(null);
  const { setMapInstance, setDrawInstance, currentMode, modelSelect, parameterSelect } = useWeather();

  useEffect(() => {
    if (!mapContainer.current) return;

    const map = new mapboxgl.Map({
      container: mapContainer.current,
      style: mapConfig.style,
      center: mapConfig.defaultCenter,
      zoom: mapConfig.defaultZoom,
    });

    mapInstance.current = map;

    // Only add draw controls if not in shipping mode
    if (modelSelect !== 'shipping') {
      const draw = new MapboxDraw({
        displayControlsDefault: false,
        controls: {
          polygon: true,
          point: true,
          trash: true,
        },
        defaultMode: currentMode === 'polygon' ? 'draw_polygon' : 'draw_point'
      });

      map.addControl(draw, 'top-right');
      drawInstance.current = draw;
    }

    const nav = new mapboxgl.NavigationControl({ visualizePitch: true });
    map.addControl(nav, 'bottom-right');

    map.on('load', () => {
      setMapInstance(map);
      if (drawInstance.current) {
        setDrawInstance(drawInstance.current);
      }
      
      // For shipping view, add shipping routes layer
      if (modelSelect === 'shipping') {
        map.addSource('shipping-routes', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [
              // Major shipping routes
              {
                type: 'Feature',
                geometry: {
                  type: 'LineString',
                  coordinates: [
                    [-80.19, 25.77], // Miami
                    [-79.93, 32.78], // Charleston
                    [-74.01, 40.71], // New York
                    [-71.05, 42.36], // Boston
                    [-63.57, 44.65]  // Halifax
                  ]
                },
                properties: { name: 'East Coast Route' }
              },
              {
                type: 'Feature',
                geometry: {
                  type: 'LineString',
                  coordinates: [
                    [-118.24, 33.73], // Los Angeles
                    [-122.42, 37.77], // San Francisco
                    [-122.33, 47.61], // Seattle
                    [-123.12, 49.28]  // Vancouver
                  ]
                },
                properties: { name: 'West Coast Route' }
              },
              {
                type: 'Feature',
                geometry: {
                  type: 'LineString',
                  coordinates: [
                    [-80.19, 25.77], // Miami
                    [-82.45, 27.95], // Tampa
                    [-90.07, 29.95], // New Orleans
                    [-95.36, 29.76]  // Houston
                  ]
                },
                properties: { name: 'Gulf Coast Route' }
              }
            ]
          }
        });

        map.addLayer({
          id: 'shipping-routes',
          type: 'line',
          source: 'shipping-routes',
          paint: {
            'line-color': '#3699ff',
            'line-width': 2,
            'line-dasharray': [2, 1]
          }
        });

        // Add port markers
        const ports = [
          { name: 'Miami', coordinates: [-80.19, 25.77] },
          { name: 'New York', coordinates: [-74.01, 40.71] },
          { name: 'Los Angeles', coordinates: [-118.24, 33.73] },
          { name: 'Houston', coordinates: [-95.36, 29.76] }
        ];

        ports.forEach(port => {
          const el = document.createElement('div');
          el.className = 'port-marker';
          el.style.width = '12px';
          el.style.height = '12px';
          el.style.borderRadius = '50%';
          el.style.backgroundColor = '#3699ff';
          el.style.border = '2px solid white';

          new mapboxgl.Marker(el)
            .setLngLat(port.coordinates)
            .setPopup(new mapboxgl.Popup().setHTML(`<h3>${port.name} Port</h3>`))
            .addTo(map);
        });

        // Set view to show all routes
        map.fitBounds([
          [-125, 25], // Southwest coordinates
          [-70, 50]   // Northeast coordinates
        ], {
          padding: 50
        });
      } else {
        initializeMapBounds(map);
      }
    });

    return () => {
      map.remove();
    };
  }, [modelSelect]);

  useEffect(() => {
    if (drawInstance.current && modelSelect !== 'shipping') {
      drawInstance.current.changeMode(
        currentMode === 'polygon' ? 'draw_polygon' : 'draw_point'
      );
    }
  }, [currentMode]);

  // Don't render map for drought model
  if (modelSelect === 'drought') {
    return null;
  }

  return (
    <div ref={mapContainer} className="w-full h-full">
      {mapInstance.current && (
        modelSelect === 'shipping' ? (
          <TropicalCycloneLayer map={mapInstance.current} />
        ) : modelSelect === 'xweather' ? (
          parameterSelect === 'tropicalcyclones' ? 
            <TropicalCycloneLayer map={mapInstance.current} /> :
            <StormReportsLayer map={mapInstance.current} />
        ) : (
          <OpenMeteoLayer map={mapInstance.current} />
        )
      )}
    </div>
  );
}